import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './Management2.css'; // Assuming this CSS file exists and is needed
import { FaEdit, FaPlus, FaSave, FaTrash, FaTimes, FaBookmark, FaLayerGroup, FaMinusCircle, FaCalendarAlt, FaClock, FaTable, FaListAlt, FaUserPlus, FaUsers } from 'react-icons/fa';
import "./Tablereservation.css"; // Assuming this CSS file exists and is needed

// --- Restaurant Configuration ---
const RESTAURANT_OPEN_TIME_STR = "10:00";
const RESTAURANT_CLOSE_TIME_STR = "23:00";

// --- Booking Configuration ---
const DEFAULT_SUGGESTED_BOOKING_DURATION_MINUTES = 90;
const DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES = 15;
const DEFAULT_WALK_IN_SEATING_DURATION_MINUTES = 90;

const tableStatuses = ['Available', 'Reserved', 'Occupied', 'Cleaning', 'Maintenance'];
const statusColors = {
    Available: '#5cb85c', Reserved: '#f0ad4e', Occupied: '#d9534f',
    Cleaning: '#5bc0de', Maintenance: '#777777',
};
const waitlistStatusColors = {
    waiting: '#6c757d',
    notified: '#17a2b8',
    seated: '#28a745',
    cancelled: '#dc3545'
};


const MAX_MINUTES_IN_DAY = 24 * 60;

const getTodayDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getDefaultStartTime = () => {
    const d = new Date(); let m = d.getMinutes();
    if (m === 0) {} else if (m <= 15) m = 15; else if (m <= 30) m = 30;
    else if (m <= 45) m = 45; else { m = 0; d.setHours(d.getHours() + 1); }
    let h = d.getHours();
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const calculateEndTimeFromDuration = (startTimeStr, durationMinutes) => {
    if (!startTimeStr || timeToMinutes(startTimeStr) === null || durationMinutes == null || isNaN(durationMinutes) || durationMinutes < 0) return null;
    const [hours, minutes] = startTimeStr.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    const startDate = new Date(2000, 0, 1, hours, minutes);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    return `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
};

const timeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string' || !timeStr.includes(':')) return null;
    const parts = timeStr.split(':');
    if (parts.length !== 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return hours * 60 + minutes;
};

const isTimeInInterval = (timeMinutes, intervalStartMinutes, intervalEndMinutes) => {
    if (timeMinutes === null || intervalStartMinutes === null || intervalEndMinutes === null) return false;
    if (intervalStartMinutes <= intervalEndMinutes) {
        return timeMinutes >= intervalStartMinutes && timeMinutes < intervalEndMinutes;
    } else { // Interval wraps around midnight
        return (timeMinutes >= intervalStartMinutes && timeMinutes < MAX_MINUTES_IN_DAY) ||
               (timeMinutes >= 0 && timeMinutes < intervalEndMinutes);
    }
};

const isTimeOverlap = (start1Str, end1Str, start2Str, end2Str) => {
    const s1 = timeToMinutes(start1Str);
    const e1 = timeToMinutes(end1Str);
    const s2 = timeToMinutes(start2Str);
    const e2 = timeToMinutes(end2Str);

    if (s1 === null || e1 === null || s2 === null || e2 === null) return false;

    const getIntervals = (startMinutes, endMinutes) => {
        if (startMinutes === endMinutes) return [];
        if (startMinutes < endMinutes) {
            return [{ start: startMinutes, end: endMinutes }];
        } else {
            return [
                { start: startMinutes, end: MAX_MINUTES_IN_DAY },
                { start: 0, end: endMinutes }
            ];
        }
    };

    const intervals1 = getIntervals(s1, e1);
    const intervals2 = getIntervals(s2, e2);

    for (const i1 of intervals1) {
        for (const i2 of intervals2) {
            if (Math.max(i1.start, i2.start) < Math.min(i1.end, i2.end)) {
                return true;
            }
        }
    }
    return false;
};


const generateOriginalIndividualTables = (uniqueBaseId, total, capacityValue, floorSpecificCleaningDuration, reservedCount = 0, occupiedCount = 0) => {
    const tables = [];
    const mockBookingDuration = DEFAULT_SUGGESTED_BOOKING_DURATION_MINUTES;

    for (let i = 0; i < total; i++) {
        const tableId = `${uniqueBaseId}-t${i + 1}-${Math.random().toString(36).substr(2, 5)}`;
        const initialBookings = [];

        if (reservedCount > 0 && i < reservedCount) {
            const startTime = `19:${String(i * 15 % 60).padStart(2, '0')}`;
            const endTime = calculateEndTimeFromDuration(startTime, mockBookingDuration);
            if(startTime && endTime) {
                initialBookings.push({
                    id: `res-${tableId}-${i}-${Date.now()}`,
                    date: getTodayDate(), time: startTime, endTime: endTime,
                    name: `AutoGuest ${i + 1}`, phone: '000000000', notes: 'Mock Reservation'
                });
            }
        } else if (occupiedCount > 0 && i >= reservedCount && i < (reservedCount + occupiedCount)) {
            const startTime = `20:${String(i * 15 % 60).padStart(2, '0')}`;
            const endTime = calculateEndTimeFromDuration(startTime, mockBookingDuration);
            if(startTime && endTime) {
                initialBookings.push({
                    id: `occ-${tableId}-${i}-${Date.now()}`,
                    date: getTodayDate(), time: startTime, endTime: endTime,
                    name: `AutoOcc ${i + 1}`, phone: '111111111', notes: 'Mock Occupancy'
                });
            }
        }

        tables.push({
            id: tableId,
            status: 'Available',
            bookings: initialBookings,
            capacityValue: capacityValue,
            floorDefaultCleaningDuration: floorSpecificCleaningDuration
        });
    }
    return tables;
};

const rawInitialMockData = [
    { id: 1, floor: 'Ground Floor', capacity: '2', totalTables: 4, reserved: 1, occupied: 0, cleaningDuration: 10 },
    { id: '1b', floor: 'Ground Floor', capacity: '4', totalTables: 2, reserved: 0, occupied: 0, cleaningDuration: 10 },
    { id: 2, floor: 'First Floor', capacity: '4', totalTables: 3, reserved: 0, occupied: 1, cleaningDuration: 15 },
    { id: '2b', floor: 'First Floor', capacity: '2', totalTables: 1, reserved: 0, occupied: 0 },
    { id: 3, floor: 'Second Floor', capacity: '6', totalTables: 2, reserved: 1, occupied: 0, cleaningDuration: 20 },
    { id: '3b', floor: 'Second Floor', capacity: '2', totalTables: 3, reserved: 0, occupied: 0, cleaningDuration: 20 },
    { id: 4, floor: 'Rooftop Terrace', capacity: '2', totalTables: 0 }
];

const transformRawDataToFloorStructure = (rawData) => {
    const floorsMap = new Map();
    let floorCounterForId = 1;

    rawData.forEach(rawGroup => {
        const floorKey = rawGroup.floor.trim().toLowerCase();
        const floorCleaningDuration = rawGroup.cleaningDuration !== undefined
            ? parseInt(rawGroup.cleaningDuration, 10)
            : DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES;

        if (!floorsMap.has(floorKey)) {
            floorsMap.set(floorKey, {
                id: `floor-${floorCounterForId++}-${Date.now() % 10000}`,
                floorName: rawGroup.floor,
                configurations: [],
                isAdded: true,
                defaultCleaningDuration: floorCleaningDuration,
            });
        }
        const floorObj = floorsMap.get(floorKey);
        const effectiveCleaningDurationForTables = floorObj.defaultCleaningDuration;
        const configId = `config-${floorObj.id}-cap${rawGroup.capacity}-${floorObj.configurations.length + 1}-${Math.random().toString(36).substring(2, 7)}`;
        const numTables = parseInt(rawGroup.totalTables, 10) || 0;

        floorObj.configurations.push({
            id: configId,
            capacity: rawGroup.capacity,
            totalTables: numTables,
            individualTables: generateOriginalIndividualTables(
                configId, numTables, rawGroup.capacity,
                effectiveCleaningDurationForTables,
                parseInt(rawGroup.reserved, 10) || 0, parseInt(rawGroup.occupied, 10) || 0
            ),
            parentFloorId: floorObj.id,
        });
    });
    return Array.from(floorsMap.values());
};

let nextFloorIdCounter = transformRawDataToFloorStructure(rawInitialMockData).length + 1;
let nextWaitlistIdCounter = 1;
const generateWaitlistId = () => `wl-${nextWaitlistIdCounter++}-${Date.now() % 10000}`;

const StatusLegend = () => (
    <div style={legendContainerStyle}>
        <h4 style={legendTitleStyle}>Table Status Legend:</h4>
        {tableStatuses.map(status => (
            <div key={status} style={legendItemStyle}>
                <span style={{ ...legendColorBoxStyle, backgroundColor: statusColors[status] }}></span>
                {status}
            </div>
        ))}
    </div>
);

const formatReservationDetailsForDisplay = (details, includeDate = true) => {
    if (!details || typeof details !== 'object') return details || "";
    let displayString = `Name: ${details.name || 'N/A'}`;
    if (details.phone) displayString += `, Phone: ${details.phone}`;
    if (includeDate && details.date) displayString += `, Date: ${details.date}`;
    if (details.time && details.endTime) {
        displayString += `, Time: ${details.time} - ${details.endTime}`;
    }
    if (details.notes) displayString += `, Notes: ${details.notes}`;
    return displayString;
};


const isTableAvailableForBooking = (tableBookingsOnDate, newBookingStartTime, newBookingEndTime, cleaningDurationToUse) => {
    if (!newBookingStartTime || !newBookingEndTime || timeToMinutes(newBookingStartTime) === null || timeToMinutes(newBookingEndTime) === null) {
        console.error("isTableAvailableForBooking called with invalid new booking times.");
        return false;
    }

    for (const existingBooking of tableBookingsOnDate) {
        if (!existingBooking.time || !existingBooking.endTime) continue;

        const existingBookingActualEndTimeStr = existingBooking.endTime;
        const existingBookingBlockedUntilAfterCleaningStr = calculateEndTimeFromDuration(existingBooking.endTime, cleaningDurationToUse);

        if (!existingBookingBlockedUntilAfterCleaningStr) {
            console.warn("Could not calculate end of cleaning for existing booking:", existingBooking);
            if (isTimeOverlap(newBookingStartTime, newBookingEndTime, existingBooking.time, existingBookingActualEndTimeStr)) {
                return false;
            }
            continue;
        }
        if (isTimeOverlap(newBookingStartTime, newBookingEndTime, existingBooking.time, existingBookingBlockedUntilAfterCleaningStr)) {
            return false;
        }
    }
    return true;
};

const getEffectiveTableStatus = (table, selectedGlobalDate, selectedGlobalTime, floorSpecificCleaningDuration, actualCurrentTimeStr, actualCurrentDateStr) => {
    const inherentStatus = table.status;

    if (inherentStatus === 'Maintenance') {
        return 'Maintenance';
    }

    const filterTimeInMinutes = selectedGlobalTime ? timeToMinutes(selectedGlobalTime) : null;
    const isTodayReal = selectedGlobalDate === actualCurrentDateStr;
    const actualNowTimeInMinutes = isTodayReal && actualCurrentTimeStr ? timeToMinutes(actualCurrentTimeStr) : null;

    const cleaningDurationToUse = floorSpecificCleaningDuration !== undefined
        ? floorSpecificCleaningDuration
        : DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES;

    const bookingsForSelectedDate = table.bookings
        .filter(b => b.date === selectedGlobalDate && b.time && b.endTime)
        .sort((a, b) => (timeToMinutes(a.time) ?? Infinity) - (timeToMinutes(b.time) ?? Infinity));

    if (filterTimeInMinutes !== null) {
        for (const booking of bookingsForSelectedDate) {
            const resStartTimeInMinutes = timeToMinutes(booking.time);
            const resEndTimeInMinutes = timeToMinutes(booking.endTime);
            if (resStartTimeInMinutes === null || resEndTimeInMinutes === null) continue;

            if (isTimeInInterval(filterTimeInMinutes, resStartTimeInMinutes, resEndTimeInMinutes)) {
                if (selectedGlobalDate > actualCurrentDateStr) return 'Reserved';
                if (selectedGlobalDate === actualCurrentDateStr) {
                    return (actualNowTimeInMinutes !== null && actualNowTimeInMinutes < resStartTimeInMinutes) ? 'Reserved' : 'Occupied';
                }
                return 'Occupied';
            }

            const cleaningEndTimeStr = calculateEndTimeFromDuration(booking.endTime, cleaningDurationToUse);
            const cleaningEndTimeInMinutes = timeToMinutes(cleaningEndTimeStr);

            if (cleaningEndTimeInMinutes !== null && isTimeInInterval(filterTimeInMinutes, resEndTimeInMinutes, cleaningEndTimeInMinutes)) {
                if (selectedGlobalDate > actualCurrentDateStr) return 'Cleaning';
                if (selectedGlobalDate === actualCurrentDateStr) {
                     if (actualNowTimeInMinutes !== null && actualNowTimeInMinutes < resStartTimeInMinutes && filterTimeInMinutes > resEndTimeInMinutes) {
                     }
                }
                return 'Cleaning';
            }
        }
        if (inherentStatus !== 'Available' && inherentStatus !== 'Reserved' && inherentStatus !== 'Occupied') return inherentStatus;
        return 'Available';

    } else {
        if (isTodayReal && actualNowTimeInMinutes !== null) {
            for (const booking of bookingsForSelectedDate) {
                const resStartTimeInMinutes = timeToMinutes(booking.time);
                const resEndTimeInMinutes = timeToMinutes(booking.endTime);
                if (resStartTimeInMinutes === null || resEndTimeInMinutes === null) continue;

                if (isTimeInInterval(actualNowTimeInMinutes, resStartTimeInMinutes, resEndTimeInMinutes)) {
                    return 'Occupied';
                }

                const cleaningEndTimeStr = calculateEndTimeFromDuration(booking.endTime, cleaningDurationToUse);
                const cleaningEndTimeInMinutes = timeToMinutes(cleaningEndTimeStr);
                 if (cleaningEndTimeInMinutes !== null && isTimeInInterval(actualNowTimeInMinutes, resEndTimeInMinutes, cleaningEndTimeInMinutes)) {
                    return 'Cleaning';
                }
            }
        }
        if (bookingsForSelectedDate.length > 0) {
            if (selectedGlobalDate < actualCurrentDateStr) return 'Occupied';
            return 'Reserved';
        }
        return inherentStatus;
    }
};
const getShortTableIdentifier = (tableId) => {
    const parts = tableId.split('-t');
    if (parts.length > 1) {
        const numPart = parts[1].split('-')[0];
        return `T${numPart}`;
    }
    const simpleParts = tableId.split('-');
    if (simpleParts.length > 1 && simpleParts[simpleParts.length-2]) return simpleParts[simpleParts.length-2].slice(0,1).toUpperCase() + simpleParts[simpleParts.length-1];
    return tableId.slice(-6);
};


const AllBookingsView = ({ bookings, tableHeaderStyle, tableCellStyle, evenRowStyle, oddRowStyle, paginationButtonStyle }) => {
    const [currentBookingsPage, setCurrentBookingsPage] = useState(1);
    const bookingsPerPage = 10;

    const indexOfLastBooking = currentBookingsPage * bookingsPerPage;
    const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
    const currentBookingsToShow = bookings.slice(indexOfFirstBooking, indexOfLastBooking);
    const totalBookingPages = Math.ceil(bookings.length / bookingsPerPage);

    useEffect(() => {
        setCurrentBookingsPage(1);
    }, [bookings]);

    if (bookings.length === 0) {
        return <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '16px', color: '#555' }}>No customer bookings found to display.</p>;
    }

    return (
        <div style={{ marginTop: '20px', overflowX: 'auto' }}>
            {/* <h3 style={{ textAlign: 'center', marginBottom: '15px', color: '#333', fontSize: '20px', fontWeight: '600' }}>
                <FaListAlt style={{marginRight: '8px'}} />All Customer Bookings
            </h3>
            <table style={{ width: '100%', minWidth:'400px', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>S.No</th>
                        <th style={{...tableHeaderStyle, minWidth:'90px'}}>Date</th>
                        <th style={{...tableHeaderStyle, minWidth:'100px'}}>Time Slot</th>
                        <th style={{...tableHeaderStyle, width:'70px'}}>Table ID</th>
                        <th style={{...tableHeaderStyle, minWidth:'100px'}}>Floor</th>
                    </tr>
                </thead>
                <tbody>
                    {currentBookingsToShow.map((booking, index) => (
                        <tr key={booking.id} style={index % 2 === 0 ? evenRowStyle : oddRowStyle}>
                            <td style={tableCellStyle}>{indexOfFirstBooking + index + 1}</td>
                            <td style={tableCellStyle}>{booking.date}</td>
                            <td style={tableCellStyle}>{booking.time} - {booking.endTime}</td>
                            <td style={tableCellStyle}>{booking.tableShortId}</td>
                            <td style={tableCellStyle}>{booking.floorName}</td>
                        </tr>
                    ))}
                </tbody>
            </table> */}
            {bookings.length > bookingsPerPage && (
                <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom:'10px' }}>
                    <button onClick={() => setCurrentBookingsPage(p => Math.max(p - 1, 1))} disabled={currentBookingsPage === 1} style={paginationButtonStyle}>Prev</button>
                    <span style={{ margin: '0 10px', fontSize:'13px' }}>Page {currentBookingsPage} of {totalBookingPages}</span>
                    <button onClick={() => setCurrentBookingsPage(p => Math.min(p + 1, totalBookingPages))} disabled={currentBookingsPage === totalBookingPages} style={paginationButtonStyle}>Next</button>
                </div>
            )}
        </div>
    );
};

const WaitlistView = ({ waitlist, onSeatGuestRequest, onRemoveFromWaitlist, tableHeaderStyle, tableCellStyle, evenRowStyle, oddRowStyle, paginationButtonStyle }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const sortedWaitlist = useMemo(() => 
        [...waitlist].sort((a, b) => a.timestamp - b.timestamp)
    , [waitlist]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItemsToShow = sortedWaitlist.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedWaitlist.length / itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [waitlist]);

    const formatTimestamp = (timestamp) => {
        const d = new Date(timestamp);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    
    if (waitlist.length === 0) {
        return <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '16px', color: '#555' }}>Waitlist is empty.</p>;
    }

    return (
        <div style={{ marginTop: '30px', overflowX: 'auto', borderTop: '1px solid #ccc', paddingTop: '20px' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '15px', color: '#333', fontSize: '20px', fontWeight: '600' }}>
                <FaUsers style={{marginRight: '8px'}} />Current Waitlist
            </h3>
            <table style={{ width: '100%', minWidth: '380px', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>#</th>
                        <th style={tableHeaderStyle}>Name</th>
                        <th style={tableHeaderStyle}>Party Size</th>
                        <th style={tableHeaderStyle}>Phone</th>
                        <th style={tableHeaderStyle}>Waiting Since</th>
                        <th style={tableHeaderStyle}>Status</th>
                        <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentItemsToShow.map((entry, index) => (
                        <tr key={entry.id} style={index % 2 === 0 ? evenRowStyle : oddRowStyle}>
                            <td style={tableCellStyle}>{indexOfFirstItem + index + 1}</td>
                            <td style={{...tableCellStyle, textAlign:'left'}}>{entry.name}</td>
                            <td style={tableCellStyle}>{entry.partySize}</td>
                            <td style={tableCellStyle}>{entry.phone || 'N/A'}</td>
                            <td style={tableCellStyle}>{formatTimestamp(entry.timestamp)}</td>
                            <td style={{...tableCellStyle, fontWeight:'bold', color: waitlistStatusColors[entry.status] || '#000'}}>
                                {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                            </td>
                            <td style={tableCellStyle}>
                                {entry.status === 'waiting' && (
                                    <button 
                                        onClick={() => onSeatGuestRequest(entry.id)} 
                                        style={{...smallActionButtonStyle, backgroundColor: statusColors.Available, color: 'white', marginRight:'5px'}}
                                        title="Seat this Guest"
                                    >Seat</button>
                                )}
                                {entry.status !== 'seated' && (
                                    <button 
                                        onClick={() => onRemoveFromWaitlist(entry.id)} 
                                        style={{...smallActionButtonStyle, backgroundColor: statusColors.Occupied, color: 'white'}}
                                        title="Remove from Waitlist"
                                    ><FaTimes /></button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {sortedWaitlist.length > itemsPerPage && (
                <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom:'10px' }}>
                    <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} style={paginationButtonStyle}>Prev</button>
                    <span style={{ margin: '0 10px', fontSize:'13px' }}>Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} style={paginationButtonStyle}>Next</button>
                </div>
            )}
        </div>
    );
};


const Tablereservations = () => {
    const [data, setData] = useState(() => transformRawDataToFloorStructure(rawInitialMockData));
    const [currentPage, setCurrentPage] = useState(1); // Reinstated for main table pagination
    const itemsPerPage = 10; // Reinstated

    const [showFloorEditModal, setShowFloorEditModal] = useState(false);
    const [editingFloor, setEditingFloor] = useState(null);
    const [focusedCapacity, setFocusedCapacity] = useState(null);
    const [newConfigCapacity, setNewConfigCapacity] = useState('');
    const [newConfigNumTables, setNewConfigNumTables] = useState('');

    const [showIndividualTableModal, setShowIndividualTableModal] = useState(false);
    const [editingTableInfo, setEditingTableInfo] = useState(null);

    const [showBookTableModal, setShowBookTableModal] = useState(false);
    const [bookingTableInfo, setBookingTableInfo] = useState(null);
    const [bookingCustomerName, setBookingCustomerName] = useState("");
    const [bookingPhoneNumber, setBookingPhoneNumber] = useState("");
    const [bookingDate, setBookingDate] = useState(getTodayDate());
    const [bookingStartTime, setBookingStartTime] = useState(getDefaultStartTime());
    const [bookingDurationMinutes, setBookingDurationMinutes] = useState(DEFAULT_SUGGESTED_BOOKING_DURATION_MINUTES.toString());
    const [bookingNotes, setBookingNotes] = useState("");

    const [globalSelectedDate, setGlobalSelectedDate] = useState(getTodayDate());
    const [globalSelectedTime, setGlobalSelectedTime] = useState(getDefaultStartTime());
    const [selectedFilterFloorId, setSelectedFilterFloorId] = useState('');
    const [actualTimeTick, setActualTimeTick] = useState(() => new Date());

    const [waitlist, setWaitlist] = useState([]);
    const [showAddToWaitlistModal, setShowAddToWaitlistModal] = useState(false);
    const [newWaitlistEntryName, setNewWaitlistEntryName] = useState("");
    const [newWaitlistEntryPartySize, setNewWaitlistEntryPartySize] = useState("");
    const [newWaitlistEntryPhone, setNewWaitlistEntryPhone] = useState("");

    const [showSeatWaitlistGuestModal, setShowSeatWaitlistGuestModal] = useState(false);
    const [seatingWaitlistGuestInfo, setSeatingWaitlistGuestInfo] = useState(null);
    const [selectedTableForWaitlistSeating, setSelectedTableForWaitlistSeating] = useState('');

    const [isProcessingAutoSeat, setIsProcessingAutoSeat] = useState(false);


    useEffect(() => {
        const timerId = setInterval(() => setActualTimeTick(new Date()), 60000);
        return () => clearInterval(timerId);
    }, []);

    const actualCurrentDate = useMemo(() => {
        const d = actualTimeTick;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, [actualTimeTick]);

    const actualCurrentTime = useMemo(() => {
        const d = actualTimeTick;
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }, [actualTimeTick]);

    const confirmAndSeatGuest = useCallback((guestDetails, tableDetails) => {
        const walkInStartTime = actualCurrentTime;
        const walkInEndTime = calculateEndTimeFromDuration(walkInStartTime, DEFAULT_WALK_IN_SEATING_DURATION_MINUTES);
    
        if (!walkInEndTime) {
            console.error("Error determining seating end time.");
            return false;
        }
        
        const seatingStartActualDateTime = new Date(`${actualCurrentDate}T${walkInStartTime}`);
        const seatingEndActualDateTime = new Date(seatingStartActualDateTime.getTime() + DEFAULT_WALK_IN_SEATING_DURATION_MINUTES * 60000);
    
        const opWindowStartForSeatingDate = new Date(`${actualCurrentDate}T${RESTAURANT_OPEN_TIME_STR}`);
        let opWindowEndForSeatingDate = new Date(`${actualCurrentDate}T${RESTAURANT_CLOSE_TIME_STR}`);
        if (timeToMinutes(RESTAURANT_CLOSE_TIME_STR) < timeToMinutes(RESTAURANT_OPEN_TIME_STR)) {
            opWindowEndForSeatingDate.setDate(opWindowEndForSeatingDate.getDate() + 1);
        }
    
        if (seatingStartActualDateTime < opWindowStartForSeatingDate) {
            console.warn(`Seating attempt for ${guestDetails.name} failed: before restaurant opening time.`);
            return false; 
        }
        if (seatingEndActualDateTime > opWindowEndForSeatingDate) {
            console.warn(`Seating attempt for ${guestDetails.name} failed: exceeds restaurant closing time.`);
            return false;
        }
    
        const newBooking = {
            id: `walkin-${guestDetails.id || guestDetails.guestId}-${Date.now()}`,
            name: guestDetails.name,
            phone: guestDetails.phone || '',
            date: actualCurrentDate,
            time: walkInStartTime,
            endTime: walkInEndTime,
            notes: `Seated from waitlist. Party of ${guestDetails.partySize || guestDetails.guestPartySize}.${guestDetails.isAutoSeated ? ' (Auto-seated)' : ''}`,
        };
    
        setData(prevData => prevData.map(floor => {
            if (floor.id === tableDetails.floorId) {
                return {
                    ...floor,
                    configurations: floor.configurations.map(config =>
                        config.id === tableDetails.configId ? {
                            ...config,
                            individualTables: config.individualTables.map(table =>
                                table.id === (tableDetails.id || tableDetails.tableId) ? {
                                    ...table,
                                    bookings: [...table.bookings, newBooking].sort((a, b) => (timeToMinutes(a.time) ?? Infinity) - (timeToMinutes(b.time) ?? Infinity))
                                } : table
                            )
                        } : config
                    )
                };
            }
            return floor;
        }));
    
        setWaitlist(prevWaitlist => prevWaitlist.map(wlEntry =>
            wlEntry.id === (guestDetails.id || guestDetails.guestId) ? { ...wlEntry, status: 'seated' } : wlEntry
        ));
        
        return true;
    }, [actualCurrentTime, actualCurrentDate, setData, setWaitlist]);


    useEffect(() => {
        if (isProcessingAutoSeat) return; 

        if (showBookTableModal || showIndividualTableModal || showFloorEditModal || 
            showAddToWaitlistModal || showSeatWaitlistGuestModal) {
            return; 
        }

        const waitingGuests = waitlist.filter(g => g.status === 'waiting').sort((a, b) => a.timestamp - b.timestamp);
        if (!waitingGuests.length) return;

        const allCurrentlyAvailableTables = [];
        data.forEach(floor => {
            floor.configurations.forEach(config => {
                config.individualTables.forEach(table => {
                    const status = getEffectiveTableStatus(
                        table, 
                        actualCurrentDate, 
                        actualCurrentTime, 
                        floor.defaultCleaningDuration,
                        actualCurrentTime,
                        actualCurrentDate
                    );
                    if (status === 'Available') {
                        allCurrentlyAvailableTables.push({ 
                            id: table.id, 
                            shortId: getShortTableIdentifier(table.id),
                            capacityValue: table.capacityValue, 
                            floorId: floor.id, 
                            floorName: floor.floorName,
                            configId: config.id
                        });
                    }
                });
            });
        });
        allCurrentlyAvailableTables.sort((a,b) => parseInt(a.capacityValue) - parseInt(b.capacityValue) || a.shortId.localeCompare(b.shortId));

        if (!allCurrentlyAvailableTables.length) return;

        let seatedThisCycle = false;

        for (const guest of waitingGuests) {
            if (seatedThisCycle) break;
            for (const availableTable of allCurrentlyAvailableTables) {
                if (parseInt(availableTable.capacityValue) >= guest.partySize) {
                    setIsProcessingAutoSeat(true); 

                    const guestDetailsForSeating = { 
                        id: guest.id, name: guest.name, 
                        partySize: guest.partySize, phone: guest.phone,
                        isAutoSeated: true 
                    };
                                    
                    const success = confirmAndSeatGuest(guestDetailsForSeating, availableTable);
                    
                    if (success) {
                         alert(`AUTOMATIC SEATING: Guest ${guestDetailsForSeating.name} (Party: ${guestDetailsForSeating.partySize}) has been seated at Table ${availableTable.shortId} on ${availableTable.floorName}.`);
                         seatedThisCycle = true; 
                    }
                    
                    setIsProcessingAutoSeat(false);
                    if (seatedThisCycle) break;
                }
            }
            if (seatedThisCycle) break;
        }
    }, [
        data, waitlist, actualCurrentDate, actualCurrentTime,
        showBookTableModal, showIndividualTableModal, showFloorEditModal, 
        showAddToWaitlistModal, showSeatWaitlistGuestModal,
        isProcessingAutoSeat, confirmAndSeatGuest,
        globalSelectedDate, globalSelectedTime // Keep these if getEffectiveTableStatus inside needs them for some edge cases, though for current avail it should use actuals
    ]);


    const processedDataForDisplay = useMemo(() => {
        let sourceFloors = [...data];
        if (selectedFilterFloorId && selectedFilterFloorId !== '') {
            sourceFloors = sourceFloors.filter(floor => floor.id === selectedFilterFloorId);
        }
        const expandedRows = [];
        sourceFloors.forEach(floor => {
            const configsByCapacity = new Map();
            floor.configurations.forEach(config => {
                if (!configsByCapacity.has(config.capacity)) configsByCapacity.set(config.capacity, []);
                configsByCapacity.get(config.capacity).push(config);
            });
            if (configsByCapacity.size === 0) {
                expandedRows.push({ uniqueRowId: `${floor.id}-no_config_placeholder`, floor, capacity: 'N/A', configurations: [], isPlaceholder: true });
            } else {
                const sortedCapacities = Array.from(configsByCapacity.keys()).sort((a, b) => parseInt(a) - parseInt(b));
                sortedCapacities.forEach(capacity => {
                    expandedRows.push({ uniqueRowId: `${floor.id}-cap-${capacity}`, floor, capacity, configurations: configsByCapacity.get(capacity), isPlaceholder: false });
                });
            }
        });
        return expandedRows;
    }, [data, selectedFilterFloorId]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = processedDataForDisplay.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        const totalPages = Math.ceil(processedDataForDisplay.length / itemsPerPage);
        if (totalPages === 0) setCurrentPage(1);
        else if (currentPage > totalPages) setCurrentPage(totalPages);
        else if (currentPage === 0 && totalPages > 0) setCurrentPage(1);
    }, [processedDataForDisplay, itemsPerPage, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedFilterFloorId, globalSelectedDate, globalSelectedTime]);


    const getRowTableStats = useCallback((rowItem) => {
        let total = 0; let available = 0;
        rowItem.configurations.forEach(config => {
            total += config.totalTables;
            config.individualTables.forEach(t => {
                const status = getEffectiveTableStatus(
                    t, globalSelectedDate, globalSelectedTime,
                    rowItem.floor.defaultCleaningDuration,
                    actualCurrentTime, actualCurrentDate
                );
                if (status === 'Available') {
                    available++;
                }
            });
        });
        return { total, available };
    }, [data, globalSelectedDate, globalSelectedTime, actualCurrentTime, actualCurrentDate]);

    const allBookingsData = useMemo(() => {
        const bookings = [];
        data.forEach(floor => {
            floor.configurations.forEach(config => {
                config.individualTables.forEach(table => {
                    const tableShortId = getShortTableIdentifier(table.id);
                    table.bookings.forEach(booking => {
                        bookings.push({
                            ...booking, 
                            floorName: floor.floorName,
                            tableShortId: tableShortId,
                            tableIdFull: table.id,
                            tableCapacity: table.capacityValue,
                        });
                    });
                });
            });
        });
        return bookings.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (dateA < dateB) return -1;
            if (dateA > dateB) return 1;
            const timeA = timeToMinutes(a.time || '00:00');
            const timeB = timeToMinutes(b.time || '00:00');
            return (timeA ?? 0) - (timeB ?? 0);
        });
    }, [data]);


    const handleAddFloorRow = () => {
        const newFloorGeneratedId = `new-floor-${nextFloorIdCounter++}-${Date.now() % 1000}`;
        const newFloor = {
            id: newFloorGeneratedId,
            floorName: `New Floor ${nextFloorIdCounter - 1}`,
            configurations: [],
            isAdded: false,
            defaultCleaningDuration: DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES
        };
        setEditingFloor(newFloor);
        setFocusedCapacity(null);
        setNewConfigCapacity(''); setNewConfigNumTables('');
        setShowFloorEditModal(true);
    };

    const handleEditFloorSetup = (floorId, capacityContext) => {
        const floorToEdit = data.find(f => f.id === floorId);
        if (floorToEdit) {
            const floorWithDefaults = {
                ...floorToEdit,
                defaultCleaningDuration: floorToEdit.defaultCleaningDuration !== undefined ? floorToEdit.defaultCleaningDuration : DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES,
            };
            setEditingFloor(JSON.parse(JSON.stringify(floorWithDefaults)));
            setFocusedCapacity(capacityContext);
            setNewConfigCapacity('');
            setNewConfigNumTables('');
            setShowFloorEditModal(true);
        }
    };


    const handleAddConfigurationToEditingFloor = () => {
        if (!editingFloor) return;
        const capacity = newConfigCapacity.trim(); const numTables = parseInt(newConfigNumTables, 10);
        if (!capacity || isNaN(parseInt(capacity, 10)) || parseInt(capacity, 10) <= 0) { alert("Valid capacity is required."); return; }
        if (isNaN(numTables) || numTables <= 0) { alert("Valid number of tables is required."); return; }
        if (editingFloor.configurations.some(conf => conf.capacity === capacity)) { alert(`Configuration for capacity ${capacity} already exists.`); return; }

        const newConfigId = `config-${editingFloor.id}-cap${capacity}-${Date.now() % 10000}`;
        const newConfiguration = {
            id: newConfigId, capacity, totalTables: numTables,
            individualTables: generateOriginalIndividualTables(
                newConfigId,
                numTables,
                capacity,
                editingFloor.defaultCleaningDuration !== undefined ? editingFloor.defaultCleaningDuration : DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES
            ),
            parentFloorId: editingFloor.id
        };
        setEditingFloor(prev => ({ ...prev, configurations: [...prev.configurations, newConfiguration] }));
        setNewConfigCapacity(''); setNewConfigNumTables('');
    };

    const handleRemoveConfigurationFromEditingFloor = (configIdToRemove) => {
        if (!editingFloor || !window.confirm("Remove this entire table configuration group (all tables of this capacity)?")) return;
        setEditingFloor(prev => ({ ...prev, configurations: prev.configurations.filter(c => c.id !== configIdToRemove) }));
    };

    const handleIndividualTableStatusChangeInEditingFloorConfig = (configId, tableId, newStatus) => {
        setEditingFloor(prev => {
            if (!prev) return null;
            const updatedConfigurations = prev.configurations.map(config => {
                if (config.id === configId) {
                    const newIndividualTables = config.individualTables.map(table => {
                        if (table.id === tableId) {
                            return { ...table, status: newStatus };
                        }
                        return table;
                    });
                    return { ...config, individualTables: newIndividualTables };
                }
                return config;
            });
            return { ...prev, configurations: updatedConfigurations };
        });
    };

    const handleRemoveIndividualTableFromEditingFloorConfig = (configId, tableIdToRemove) => {
        if (!editingFloor || !window.confirm("Remove this individual table from the configuration?")) return;
        setEditingFloor(prev => {
            if (!prev) return null;
            const updatedConfigurations = prev.configurations.map(config => {
                if (config.id === configId) {
                    const newIndividualTables = config.individualTables.filter(t => t.id !== tableIdToRemove);
                    return {
                        ...config,
                        individualTables: newIndividualTables,
                        totalTables: newIndividualTables.length,
                    };
                }
                return config;
            });
            return { ...prev, configurations: updatedConfigurations };
        });
    };

    const handleAddIndividualTableToEditingFloorConfig = (configId) => {
        if (!editingFloor) return;
        setEditingFloor(prev => {
            if (!prev) return null;
            const updatedConfigurations = prev.configurations.map(config => {
                if (config.id === configId) {
                    let maxTNum = 0;
                    config.individualTables.forEach(t => {
                        const match = t.id.match(/-t(\d+)-/);
                        if (match && parseInt(match[1]) > maxTNum) {
                            maxTNum = parseInt(match[1]);
                        }
                    });
                    const nextTNum = config.individualTables.length > 0 ? maxTNum + 1 : 1;

                    const newTableId = `${config.id}-t${nextTNum}-${Math.random().toString(36).substr(2, 5)}`;
                    const newTable = {
                        id: newTableId,
                        status: 'Available',
                        bookings: [],
                        capacityValue: config.capacity,
                        floorDefaultCleaningDuration: prev.defaultCleaningDuration !== undefined
                            ? prev.defaultCleaningDuration
                            : DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES,
                    };
                    const newIndividualTables = [...config.individualTables, newTable];
                    return {
                        ...config,
                        individualTables: newIndividualTables,
                        totalTables: newIndividualTables.length,
                    };
                }
                return config;
            });
            return { ...prev, configurations: updatedConfigurations };
        });
    };

    const submitFloorEdit = () => {
        if (!editingFloor || !editingFloor.floorName.trim()) { alert("Floor name is required."); return; }

        const cleanDurValue = editingFloor.defaultCleaningDuration;
        const cleanDur = (cleanDurValue !== undefined && !isNaN(parseInt(cleanDurValue,10)) && parseInt(cleanDurValue,10) >= 0)
                        ? parseInt(cleanDurValue, 10)
                        : DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES;

        if (isNaN(cleanDur) || cleanDur < 0) {
            alert("Valid Cleaning Duration (minutes) is required for the floor.");
            return;
        }

        const floorToSave = { ...editingFloor, isAdded: true, defaultCleaningDuration: cleanDur };
        floorToSave.configurations = floorToSave.configurations.map(config => ({
            ...config,
            totalTables: config.individualTables.length,
            individualTables: config.individualTables.map(it => ({...it, floorDefaultCleaningDuration: cleanDur }))
        }));

        setData(prev => {
            const idx = prev.findIndex(f => f.id === floorToSave.id);
            if (idx > -1) { const nd = [...prev]; nd[idx] = floorToSave; return nd.sort((a, b) => a.floorName.localeCompare(b.floorName)); }
            else { return [floorToSave, ...prev].sort((a, b) => a.floorName.localeCompare(b.floorName)); }
        });
        setShowFloorEditModal(false); setEditingFloor(null); setFocusedCapacity(null);
    };

    const handleDeleteFloorRow = (floorId) => {
        if (!window.confirm("Delete this entire floor and all its configurations?")) return;
        setData(prevData => prevData.filter(f => f.id !== floorId));
        if (selectedFilterFloorId === floorId) {
            setSelectedFilterFloorId('');
        }
    };

    const handleIndividualTableClick = (floorId, configId, tableClicked) => {
        const currentFloor = data.find(f => f.id === floorId);
        if (!currentFloor) { console.error("Floor not found"); return;}

        const effectiveStatus = getEffectiveTableStatus(
            tableClicked,
            globalSelectedDate,
            globalSelectedTime,
            currentFloor.defaultCleaningDuration,
            actualCurrentTime,
            actualCurrentDate
        );

        if (effectiveStatus === 'Available') {
            setBookingTableInfo({ floorId, configId, tableId: tableClicked.id, capacity: tableClicked.capacityValue });
            setBookingCustomerName(""); setBookingPhoneNumber("");
            setBookingDate(globalSelectedDate);
            setBookingStartTime( (globalSelectedTime && timeToMinutes(globalSelectedTime) !== null) ? globalSelectedTime : getDefaultStartTime() );
            setBookingDurationMinutes(DEFAULT_SUGGESTED_BOOKING_DURATION_MINUTES.toString());
            setBookingNotes("");
            setShowBookTableModal(true);
        } else {
            let activeBooking = null;
            const bookingsOnDate = tableClicked.bookings.filter(b => b.date === globalSelectedDate && b.time && b.endTime);

            if (bookingsOnDate.length > 0) {
                if (globalSelectedTime && timeToMinutes(globalSelectedTime) !== null) {
                    const cleaningDur = currentFloor.defaultCleaningDuration !== undefined ? currentFloor.defaultCleaningDuration : DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES;
                    const filterTimeMins = timeToMinutes(globalSelectedTime);

                    activeBooking = bookingsOnDate.find(b => {
                        const bStartMins = timeToMinutes(b.time);
                        const bEndMins = timeToMinutes(b.endTime);
                        if (bStartMins === null || bEndMins === null || filterTimeMins === null) return false;

                        if (isTimeInInterval(filterTimeMins, bStartMins, bEndMins)) return true;

                        const cleaningEndTimeForBookingStr = calculateEndTimeFromDuration(b.endTime, cleaningDur);
                        const cleaningEndMins = timeToMinutes(cleaningEndTimeForBookingStr);
                        if (cleaningEndMins !== null && isTimeInInterval(filterTimeMins, bEndMins, cleaningEndMins)) return true;
                        return false;
                    });
                }
                if (!activeBooking && bookingsOnDate.length > 0) {
                     activeBooking = bookingsOnDate.sort((a, b) => (timeToMinutes(a.time) ?? Infinity) - (timeToMinutes(b.time) ?? Infinity))[0];
                }
            }
            setEditingTableInfo({
                floorId, configId, tableId: tableClicked.id,
                currentEffectiveStatus: effectiveStatus,
                newStatus: tableClicked.status,
                currentInherentStatus: tableClicked.status,
                currentBookingDetails: activeBooking || null,
                capacity: tableClicked.capacityValue,
                parentFloorId: currentFloor.id
            });
            setShowIndividualTableModal(true);
        }
    };

    const handleConfirmBooking = () => {
        if (!bookingTableInfo) return;
        if (!bookingCustomerName.trim()) { alert("Customer Name is required."); return; }
        if (!bookingDate) { alert("Reservation Date is required."); return; }

        const finalStartTime = bookingStartTime;
        const duration = parseInt(bookingDurationMinutes, 10);

        if (!finalStartTime || timeToMinutes(finalStartTime) === null) {
             alert("Valid Start Time (HH:MM) is required."); return;
        }
        if (isNaN(duration) || duration <= 0) {
            alert("Please enter a valid positive duration in minutes."); return;
        }

        const finalEndTime = calculateEndTimeFromDuration(finalStartTime, duration);
        if (!finalEndTime) {
            alert("Could not calculate end time. Please check start time and duration."); return;
        }
        
        const bookingStartActualDateTime = new Date(`${bookingDate}T${finalStartTime}`);
        if (isNaN(bookingStartActualDateTime.getTime())) {
            alert("Invalid booking start date or time format.");
            return;
        }
        const bookingEndActualDateTime = new Date(bookingStartActualDateTime.getTime() + duration * 60000);

        const opWindowStartForBookingDate = new Date(`${bookingDate}T${RESTAURANT_OPEN_TIME_STR}`);
        let opWindowEndForBookingDate = new Date(`${bookingDate}T${RESTAURANT_CLOSE_TIME_STR}`);

        if (timeToMinutes(RESTAURANT_CLOSE_TIME_STR) < timeToMinutes(RESTAURANT_OPEN_TIME_STR)) {
            opWindowEndForBookingDate.setDate(opWindowEndForBookingDate.getDate() + 1);
        }
        
        if (bookingStartActualDateTime < opWindowStartForBookingDate) {
            alert(`Bookings must start on or after restaurant opening time: ${RESTAURANT_OPEN_TIME_STR} (on ${bookingDate}).`);
            return;
        }
        if (bookingEndActualDateTime > opWindowEndForBookingDate) {
            const endWindowDateStr = `${opWindowEndForBookingDate.getFullYear()}-${String(opWindowEndForBookingDate.getMonth() + 1).padStart(2, '0')}-${String(opWindowEndForBookingDate.getDate()).padStart(2, '0')}`;
            const endWindowTimeStr = `${String(opWindowEndForBookingDate.getHours()).padStart(2, '0')}:${String(opWindowEndForBookingDate.getMinutes()).padStart(2, '0')}`;
            alert(`Booking duration is too long or start time is too late. All bookings must end by ${endWindowTimeStr} on ${endWindowDateStr}.`);
            return;
        }

        const { floorId, configId, tableId } = bookingTableInfo;
        const targetFloor = data.find(f => f.id === floorId);
        const targetConfig = targetFloor?.configurations.find(c => c.id === configId);
        const targetTable = targetConfig?.individualTables.find(t => t.id === tableId);

        if (!targetTable || !targetFloor) { alert("Table or floor not found!"); return; }

        const cleaningDurationToUse = targetFloor.defaultCleaningDuration !== undefined ? targetFloor.defaultCleaningDuration : DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES;
        const bookingsForNewBookingDate = targetTable.bookings.filter(b => b.date === bookingDate);

        if (!isTableAvailableForBooking(bookingsForNewBookingDate, finalStartTime, finalEndTime, cleaningDurationToUse)) {
            alert(`Table is not available for ${finalStartTime} - ${finalEndTime} on ${bookingDate}. It conflicts with an existing booking or its cleaning time.`);
            return;
        }

        const newBookingId = `booking-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const newBooking = { id: newBookingId, name: bookingCustomerName, phone: bookingPhoneNumber, date: bookingDate, time: finalStartTime, endTime: finalEndTime, notes: bookingNotes };

        setData(prevData => prevData.map(floor => {
            if (floor.id === floorId) {
                return { ...floor, configurations: floor.configurations.map(config =>
                    config.id === configId ? { ...config, individualTables: config.individualTables.map(table =>
                        table.id === tableId ? { ...table, bookings: [...table.bookings, newBooking].sort((a, b) => (timeToMinutes(a.time) ?? Infinity) - (timeToMinutes(b.time) ?? Infinity)) } : table
                    )} : config
                )};
            } return floor;
        }));
        setShowBookTableModal(false); setBookingTableInfo(null);
        alert(`Table booked for ${bookingCustomerName}. Booking ID: ${newBookingId}. Table: ${getShortTableIdentifier(tableId)}`);
    };

    const handleIndividualTableStatusChange = (e) => setEditingTableInfo(prev => ({ ...prev, newStatus: e.target.value }));

    const submitIndividualTableStatus = () => {
        if (!editingTableInfo) return;
        const { floorId, configId, tableId, newStatus, currentBookingDetails } = editingTableInfo;
        setData(prevData => prevData.map(floor => {
            if (floor.id === floorId) {
                return { ...floor, configurations: floor.configurations.map(config =>
                    config.id === configId ? { ...config, individualTables: config.individualTables.map(table => {
                        if (table.id === tableId) {
                            let updatedBookings = [...table.bookings];
                            if (newStatus === 'Available' && currentBookingDetails && currentBookingDetails.id) {
                                 const bookingAssociatedWithCurrentView = table.bookings.find(b => b.id === currentBookingDetails.id);
                                 if (bookingAssociatedWithCurrentView &&
                                     window.confirm(`Setting inherent status to 'Available'. Do you also want to cancel the currently highlighted booking for ${currentBookingDetails.name} (${currentBookingDetails.date} ${currentBookingDetails.time})? This cannot be undone.`)) {
                                     updatedBookings = table.bookings.filter(b => b.id !== currentBookingDetails.id);
                                 }
                            }
                            return { ...table, status: newStatus, bookings: updatedBookings };
                        } return table;
                    })} : config
                )};
            } return floor;
        }));
        setShowIndividualTableModal(false); 
        setEditingTableInfo(null);
    };
    
    const handleAddWaitlistGuestClick = () => {
        setNewWaitlistEntryName("");
        setNewWaitlistEntryPartySize("");
        setNewWaitlistEntryPhone("");
        setShowAddToWaitlistModal(true);
    };

    const handleSaveWaitlistEntry = () => {
        const partySizeNum = parseInt(newWaitlistEntryPartySize, 10);
        if (!newWaitlistEntryName.trim()) {
            alert("Guest name is required for the waitlist.");
            return;
        }
        if (isNaN(partySizeNum) || partySizeNum <= 0) {
            alert("Valid party size is required.");
            return;
        }

        const newEntry = {
            id: generateWaitlistId(),
            name: newWaitlistEntryName.trim(),
            partySize: partySizeNum,
            phone: newWaitlistEntryPhone.trim(),
            timestamp: Date.now(),
            status: 'waiting',
        };
        setWaitlist(prev => [...prev, newEntry]);
        setShowAddToWaitlistModal(false);
    };

    const handleRemoveFromWaitlist = (waitlistEntryId) => {
        if (window.confirm("Are you sure you want to remove this guest from the waitlist?")) {
            setWaitlist(prev => prev.filter(entry => entry.id !== waitlistEntryId));
        }
    };
    
    const handleSeatGuestRequest = (waitlistEntryId) => {
        const entryToSeat = waitlist.find(e => e.id === waitlistEntryId);
        if (entryToSeat) {
            setSeatingWaitlistGuestInfo({ entry: entryToSeat });
            setSelectedTableForWaitlistSeating('');
            setShowSeatWaitlistGuestModal(true);
        }
    };

    const availableTablesForWaitlistGuest = useMemo(() => {
        if (!seatingWaitlistGuestInfo) return [];
        const guestPartySize = seatingWaitlistGuestInfo.entry.partySize;
        const tables = [];
        data.forEach(floor => {
            floor.configurations.forEach(config => {
                config.individualTables.forEach(table => {
                    if (parseInt(table.capacityValue) >= guestPartySize) {
                        const status = getEffectiveTableStatus(
                            table,
                            actualCurrentDate, 
                            actualCurrentTime, 
                            floor.defaultCleaningDuration,
                            actualCurrentTime,
                            actualCurrentDate
                        );
                        if (status === 'Available') {
                            tables.push({
                                id: table.id,
                                shortId: getShortTableIdentifier(table.id),
                                capacityValue: table.capacityValue, 
                                floorName: floor.floorName,
                                floorId: floor.id,
                                configId: config.id,
                            });
                        }
                    }
                });
            });
        });
        return tables.sort((a,b) => parseInt(a.capacityValue) - parseInt(b.capacityValue) || a.shortId.localeCompare(b.shortId));
    }, [data, seatingWaitlistGuestInfo, actualCurrentDate, actualCurrentTime]);


    const handleConfirmSeatWaitlistGuest = () => { // Manual seating from waitlist modal
        if (!seatingWaitlistGuestInfo || !selectedTableForWaitlistSeating) {
            alert("Please select a guest and a table.");
            return;
        }
        const guestEntry = seatingWaitlistGuestInfo.entry;
        const tableInfo = availableTablesForWaitlistGuest.find(t => t.id === selectedTableForWaitlistSeating);

        if (!tableInfo) {
            alert("Selected table information not found or table is no longer available.");
            return;
        }
        
        const guestDetailsForConfirmation = { ...guestEntry, isAutoSeated: false };

        if (confirmAndSeatGuest(guestDetailsForConfirmation, tableInfo)) {
            alert(`${guestEntry.name} has been seated at table ${tableInfo.shortId}.`);
            setShowSeatWaitlistGuestModal(false);
            setSeatingWaitlistGuestInfo(null);
            setSelectedTableForWaitlistSeating('');
        } else {
            alert(`Could not seat ${guestEntry.name}. Please check restaurant operating hours or try a different time.`);
        }
    };
    
    return (
        <div style={pageContainerStyle}>
            <div style={mainTableSectionStyle} className="main-table-management-scroll-area">
                <div style={globalControlsContainerStyle}>
                    <div style={globalControlItemStyle}>
                        <label htmlFor="globalDate" style={labelStyle}><FaCalendarAlt /> Date:</label>
                        <input type="date" id="globalDate" value={globalSelectedDate} onChange={(e) => setGlobalSelectedDate(e.target.value)} style={{ ...inputFieldStyle, width: 'auto', padding: '6px 8px' }} />
                    </div>
                    <div style={globalControlItemStyle}>
                        <label htmlFor="globalTime" style={labelStyle}><FaClock /> Time:</label>
                        <input type="time" id="globalTime" value={globalSelectedTime} onChange={(e) => setGlobalSelectedTime(e.target.value || "")} style={{ ...inputFieldStyle, width: 'auto', padding: '6px 8px' }} />
                    </div>
                    <div style={globalControlItemStyle}>
                        <label htmlFor="filterFloor" style={labelStyle}><FaLayerGroup style={{ marginRight: '3px' }} /> Floor:</label>
                        <select id="filterFloor" value={selectedFilterFloorId} onChange={(e) => setSelectedFilterFloorId(e.target.value)} style={{ ...inputFieldStyle, width: '150px', padding: '6px 8px' }}>
                            <option value="">All Floors</option>
                            {data.sort((a,b) => a.floorName.localeCompare(b.floorName)).map(floor => (<option key={floor.id} value={floor.id}>{floor.floorName}</option>))}
                        </select>
                    </div>
                    <button onClick={handleAddWaitlistGuestClick} style={{ ...buttonStyle, backgroundColor: '#17a2b8', marginLeft: '10px' }} title="Add guest to waitlist">
                        <FaUserPlus style={{ marginRight: "5px" }} /> Add to Waitlist
                    </button>
                    <button onClick={handleAddFloorRow} style={{ ...buttonStyle, fontWeight: "bold", marginLeft: 'auto' }} title="Add a new floor"><FaPlus style={{ marginRight: "3px" }} />Add Floor</button>
                </div>
                <StatusLegend />

                {/* Main Table Display Restored Here */}
                <div style={{ marginTop: '10px', flexGrow: 1, overflowY: 'auto', minHeight: '100vh' }}>
                    {currentItems.length === 0 ? (
                        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '16px' }}>
                            {data.length === 0 ? "No floors available. Click 'Add Floor'." : selectedFilterFloorId && processedDataForDisplay.length === 0 ? "Selected floor has no table configurations." : "No tables match criteria / no configurations exist."}
                        </p>
                    ) : (
                        <>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={tableHeaderStyle}>S.No</th><th style={tableHeaderStyle}>Floor Name</th><th style={{ ...tableHeaderStyle, width: '80px' }}>Capacity</th>
                                    <th style={{ ...tableHeaderStyle, width: '100px' }}>Total Tables</th><th style={{ ...tableHeaderStyle, width: '120px' }}>Available (Filtered)</th>
                                    <th style={{ ...tableHeaderStyle, minWidth: '250px' }}>Tables Status & Actions</th>
                                    <th style={{ ...tableHeaderStyle, width:'100px' }}>Floor Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((rowItem, index) => {
                                    const stats = getRowTableStats(rowItem);
                                    return (
                                        <tr key={rowItem.uniqueRowId} style={index % 2 === 0 ? evenRowStyle : oddRowStyle}>
                                            <td style={tableCellStyle}>{indexOfFirstItem + index + 1}</td><td style={tableCellStyle}>{rowItem.floor.floorName}</td>
                                            <td style={tableCellStyle}>{rowItem.capacity}</td><td style={tableCellStyle}>{stats.total}</td><td style={tableCellStyle}>{stats.available}</td>
                                            <td style={{ ...tableCellStyle, textAlign: 'left', verticalAlign: 'top' }}>
                                                {rowItem.isPlaceholder ? (<span style={{fontSize: '12px', color: '#777'}}>No tables configured.</span>)
                                                : rowItem.configurations.length > 0 && stats.total > 0 ? (
                                                    <div style={tableVisualContainerStyle}>
                                                        {rowItem.configurations.flatMap(config => config.individualTables
                                                            .filter(table => table.capacityValue === rowItem.capacity)
                                                            .map(table => {
                                                            const effectiveStatus = getEffectiveTableStatus(
                                                                table, globalSelectedDate, globalSelectedTime,
                                                                rowItem.floor.defaultCleaningDuration, actualCurrentTime, actualCurrentDate
                                                            );
                                                            const shortId = getShortTableIdentifier(table.id);
                                                            return (
                                                                <div key={table.id} style={tableRepresentationWrapperStyleNoHighlight}
                                                                    title={ `Table ${shortId} (Cap: ${table.capacityValue}) - Effective: ${effectiveStatus}. Click status icon to book/manage.`}
                                                                >
                                                                    <div
                                                                        style={{ ...individualTableStyle, backgroundColor: statusColors[effectiveStatus] || statusColors['Maintenance'] }}
                                                                        onClick={() => handleIndividualTableClick(rowItem.floor.id, config.id, table)}
                                                                    >
                                                                        {shortId}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }))}
                                                    </div>
                                                ) : (<span style={{ fontSize: '12px', color: '#777' }}>No tables of capacity {rowItem.capacity} configured.</span>)}
                                            </td>
                                            <td style={tableCellStyle}>
                                                <button
                                                    style={actionButtonStyle}
                                                    onClick={() => handleEditFloorSetup(rowItem.floor.id, rowItem.isPlaceholder ? null : rowItem.capacity)}
                                                    title={rowItem.isPlaceholder ? "Edit Floor Details" : `Edit Tables for Capacity ${rowItem.capacity} on ${rowItem.floor.floorName}`}
                                                > <FaEdit /> </button>
                                                <button onClick={() => handleDeleteFloorRow(rowItem.floor.id)} style={{ ...actionButtonStyle, color: '#d9534f' }} title="Delete Floor"><FaTrash /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {processedDataForDisplay.length > itemsPerPage && (
                            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom:'10px', flexShrink: 0 }}>
                                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} style={paginationButtonStyle}>Prev</button>
                                <span style={{ margin: '0 10px' }}>Page {currentPage} of {Math.ceil(processedDataForDisplay.length / itemsPerPage)}</span>
                                <button onClick={() => setCurrentPage(p => Math.min(p + 1, Math.ceil(processedDataForDisplay.length / itemsPerPage)))} disabled={currentPage === Math.ceil(processedDataForDisplay.length / itemsPerPage)} style={paginationButtonStyle}>Next</button>
                            </div>
                        )}
                        </>
                    )}
                </div>
            </div>

            {/* <div style={bookingsViewSectionStyle} className="bookings-sidebar-scroll-area">
                <AllBookingsView
                    bookings={allBookingsData}
                    tableHeaderStyle={tableHeaderStyle} 
                    tableCellStyle={tableCellStyle}
                    evenRowStyle={evenRowStyle}
                    oddRowStyle={oddRowStyle}
                    paginationButtonStyle={paginationButtonStyle}
                />
                <WaitlistView
                    waitlist={waitlist}
                    onSeatGuestRequest={handleSeatGuestRequest}
                    onRemoveFromWaitlist={handleRemoveFromWaitlist}
                    tableHeaderStyle={tableHeaderStyle} 
                    tableCellStyle={tableCellStyle}
                    evenRowStyle={evenRowStyle}
                    oddRowStyle={oddRowStyle}
                    paginationButtonStyle={paginationButtonStyle}
                />
            </div> */}

            {/* MODALS */}
            {showFloorEditModal && editingFloor && ( <div style={{ ...modalStyle, maxWidth: '750px' }}> <div style={modalHeaderStyle}> <h3 style={{ margin: 0, fontSize: '18px' }}> {editingFloor.isAdded === false ? 'Add New Floor' : focusedCapacity ? `Edit Tables for Capacity ${focusedCapacity} on ${editingFloor.floorName}` : `Edit Floor: ${editingFloor.floorName}` } </h3> <button style={modalCloseButtonStyle} onClick={() => { setShowFloorEditModal(false); setEditingFloor(null); setFocusedCapacity(null); }} title="Close"><FaTimes /></button> </div> <div style={inputContainerStyle}> <label htmlFor="editFloorName" style={labelStyle}>Floor Name:<span style={{ color: 'red' }}>*</span></label> <input id="editFloorName" type="text" value={editingFloor.floorName} onChange={(e) => setEditingFloor({ ...editingFloor, floorName: e.target.value })} style={inputFieldStyle} /> </div> <div style={inputContainerStyle}> <label htmlFor="floorCleaningDuration" style={labelStyle}>Default Cleaning Duration (min):<span style={{ color: 'red' }}>*</span></label> <input id="floorCleaningDuration" type="number" min="0" value={editingFloor.defaultCleaningDuration === undefined ? '' : String(editingFloor.defaultCleaningDuration)} onChange={(e) => { const val = e.target.value; setEditingFloor({ ...editingFloor, defaultCleaningDuration: val === '' ? undefined : (parseInt(val,10) >= 0 ? parseInt(val,10) : 0) }); }} style={inputFieldStyle} placeholder={`e.g., ${DEFAULT_GLOBAL_CLEANING_DURATION_MINUTES}`} /> </div> <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}> <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}> {focusedCapacity ? `Manage Tables for Capacity: ${focusedCapacity}` : "Table Configurations (by Capacity):" } </h4> {editingFloor.configurations.length === 0 && !focusedCapacity && <p style={{ fontSize: '13px', color: '#555', textAlign: 'center' }}>No table configurations defined. Add one below.</p>} {(focusedCapacity ? editingFloor.configurations.filter(c => c.capacity === focusedCapacity) : editingFloor.configurations ).sort((a, b) => parseInt(a.capacity) - parseInt(b.capacity)) .map((config) => { if (focusedCapacity && config.capacity !== focusedCapacity) { return null; } return ( <div key={config.id} style={configSectionStyle}> <div style={configHeaderStyle}> <span>Capacity: <strong>{config.capacity}</strong> ({config.individualTables.length} Table{config.individualTables.length === 1 ? '' : 's'})</span> <div> <button onClick={() => handleAddIndividualTableToEditingFloorConfig(config.id)} style={{...smallButtonStyle, backgroundColor: '#28a745', color:'white', marginRight: '8px'}} title="Add a table to this configuration"><FaPlus /> Add Table</button> <button onClick={() => handleRemoveConfigurationFromEditingFloor(config.id)} style={{ ...smallButtonStyle, color: 'white', backgroundColor: '#dc3545' }} title="Remove Entire Configuration"> <FaMinusCircle /> Remove Config </button> </div> </div> {config.individualTables.length > 0 ? ( <div style={individualTableListStyleInModal}> {config.individualTables.map((table, idx) => ( <div key={table.id} style={individualTableEditRowStyle}> <span style={{ minWidth: '70px' }}>{getShortTableIdentifier(table.id)}</span> <select value={table.status} onChange={(e) => handleIndividualTableStatusChangeInEditingFloorConfig(config.id, table.id, e.target.value)} style={{ ...inputFieldStyle, width: '130px', fontSize:'12px', padding:'4px 6px', height: 'auto', marginRight:'auto', marginLeft:'10px' }} > {tableStatuses.map(s => <option key={s} value={s}>{s}</option>)} </select> <button onClick={() => handleRemoveIndividualTableFromEditingFloorConfig(config.id, table.id)} style={{ ...actionButtonStyle, fontSize: '13px', color: '#c82333', padding: '3px 5px' }} title="Delete this Table"> <FaTrash /> </button> </div> ))} </div> ) : ( <p style={{fontSize: '12px', color: '#666', paddingLeft: '10px', margin: '5px 0'}}> No tables in this configuration. Click "Add Table" to add some. </p> )} </div> ); })} {focusedCapacity && !editingFloor.configurations.some(c => c.capacity === focusedCapacity) && ( <p style={{ fontSize: '13px', color: '#777', textAlign:'center', marginTop:'10px' }}> Configuration for capacity "{focusedCapacity}" does not exist. Add it via general floor edit. </p> )} {!focusedCapacity && ( <div style={{ marginTop: '20px', borderTop: '1px dashed #ccc', paddingTop: '15px' }}> <h5 style={{ margin: '0 0 10px 0', fontSize: '15px' }}>Add New Configuration Group:</h5> <div style={inputRowStyle}> <div style={inputContainerHalfStyle}><label htmlFor="newConfigCapacity" style={labelStyle}>Capacity:<span style={{ color: 'red' }}>*</span></label><input id="newConfigCapacity" type="number" min="1" value={newConfigCapacity} onChange={(e) => setNewConfigCapacity(e.target.value)} style={inputFieldStyle} placeholder="e.g., 2, 4"/></div> <div style={inputContainerHalfStyle}><label htmlFor="newConfigNumTables" style={labelStyle}># Tables for this Capacity:<span style={{ color: 'red' }}>*</span></label><input id="newConfigNumTables" type="number" min="1" value={newConfigNumTables} onChange={(e) => setNewConfigNumTables(e.target.value)} style={inputFieldStyle} placeholder="e.g., 5"/></div> </div> <button onClick={handleAddConfigurationToEditingFloor} style={{ ...buttonStyle, height: '32px', fontSize: '13px', backgroundColor: '#17a2b8', color:'white', padding: '0 10px' }} title="Add Configuration Group"><FaLayerGroup style={{ marginRight: '5px' }} /> Add Config Group</button> </div> )} </div> <div style={buttonContainerStyle}> <button onClick={submitFloorEdit} style={{ ...buttonStyle, width: 'auto', paddingLeft:'20px', paddingRight:'20px' }}> <FaSave style={{marginRight:'8px'}}/> {editingFloor.isAdded === false ? 'Save New Floor' : 'Save Changes to Floor'} </button> </div> </div> )}
            {showBookTableModal && bookingTableInfo && ( <div style={{ ...modalStyle, maxWidth: '450px' }}> <div style={modalHeaderStyle}> <h3 style={{ margin: 0, fontSize: '18px' }}>Book Table (Cap: {bookingTableInfo.capacity})</h3> <button style={modalCloseButtonStyle} onClick={() => { setShowBookTableModal(false); setBookingTableInfo(null); }} title="Close"><FaTimes /></button> </div> <p style={{ marginBottom: '15px', fontSize: '14px' }}> Table ID: <strong>{getShortTableIdentifier(bookingTableInfo.tableId)}</strong> (Floor: {data.find(f => f.id === bookingTableInfo.floorId)?.floorName}) </p> <div style={inputRowStyle}> <div style={inputContainerHalfStyle}> <label htmlFor="bookingCustomerName" style={labelStyle}>Name:<span style={{ color: 'red' }}>*</span></label> <input id="bookingCustomerName" type="text" value={bookingCustomerName} onChange={(e) => setBookingCustomerName(e.target.value)} style={inputFieldStyle} /> </div> <div style={inputContainerHalfStyle}> <label htmlFor="bookingPhoneNumber" style={labelStyle}>Phone:</label> <input id="bookingPhoneNumber" type="tel" value={bookingPhoneNumber} onChange={(e) => setBookingPhoneNumber(e.target.value)} style={inputFieldStyle} /> </div> </div> <div style={inputRowStyle}> <div style={inputContainerHalfStyle}> <label htmlFor="bookingDate" style={labelStyle}>Date:<span style={{ color: 'red' }}>*</span></label> <input id="bookingDate" type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} style={inputFieldStyle} min={getTodayDate()} /> </div> <div style={inputContainerHalfStyle}></div> </div> <div style={inputRowStyle}> <div style={inputContainerHalfStyle}> <label htmlFor="bookingStartTime" style={labelStyle}>Start Time (HH:MM):<span style={{ color: 'red' }}>*</span></label> <input id="bookingStartTime" type="time" value={bookingStartTime} onChange={(e) => setBookingStartTime(e.target.value)} style={inputFieldStyle} /> </div> <div style={inputContainerHalfStyle}> <label htmlFor="bookingDurationMinutes" style={labelStyle}>Duration (minutes):<span style={{ color: 'red' }}>*</span></label> <input id="bookingDurationMinutes" type="number" min="15" step="15" value={bookingDurationMinutes} onChange={(e) => setBookingDurationMinutes(e.target.value)} style={inputFieldStyle} placeholder={`e.g., ${DEFAULT_SUGGESTED_BOOKING_DURATION_MINUTES}`} /> </div> </div> <p style={{fontSize:'11px', color:'#555', marginTop:'-8px', marginBottom:'10px'}}>Start Time and a positive Duration are required.</p> <p style={{fontSize:'11px', color:'#333', marginTop:'-8px', marginBottom:'10px'}}>Restaurant Hours: {RESTAURANT_OPEN_TIME_STR} - {RESTAURANT_CLOSE_TIME_STR} {timeToMinutes(RESTAURANT_CLOSE_TIME_STR) < timeToMinutes(RESTAURANT_OPEN_TIME_STR) ? "(next day)" : "(same day)"}.</p> <div style={inputContainerStyle}> <label htmlFor="bookingNotes" style={labelStyle}>Notes:</label> <textarea id="bookingNotes" value={bookingNotes} onChange={(e) => setBookingNotes(e.target.value)} style={{ ...inputFieldStyle, height: '60px', resize: 'vertical' }} /> </div> <div style={buttonContainerStyle}> <button onClick={handleConfirmBooking} style={{ ...buttonStyle, width: '180px', backgroundColor: statusColors.Reserved }}> <FaBookmark style={{ marginRight: '8px' }} /> Confirm Booking</button> </div> </div> )}
            {showIndividualTableModal && editingTableInfo && ( <div style={{ ...modalStyle, maxWidth: '450px' }}> <div style={modalHeaderStyle}> <h3 style={{ margin: 0, fontSize: '18px' }}>Manage Table (Cap: {editingTableInfo.capacity})</h3> <button style={modalCloseButtonStyle} onClick={() => { setShowIndividualTableModal(false); setEditingTableInfo(null); }} title="Close"><FaTimes /></button> </div> <p style={{ marginBottom: '5px', fontSize: '14px' }}> Table ID: <strong>{getShortTableIdentifier(editingTableInfo.tableId)}</strong> </p> <p style={{ marginBottom: '5px', fontSize: '13px' }}> Effective Status (at {globalSelectedDate} {globalSelectedTime || 'AnyTime'}): <strong style={{ color: statusColors[editingTableInfo.currentEffectiveStatus] }}>{editingTableInfo.currentEffectiveStatus}</strong> </p> <p style={{ marginBottom: '10px', fontSize: '13px' }}> Current Inherent Status: <strong>{editingTableInfo.currentInherentStatus}</strong></p> {editingTableInfo.currentBookingDetails && ( <div style={detailsBoxStyle}> <strong>Relevant Booking for {globalSelectedDate} {globalSelectedTime ? `at/around ${globalSelectedTime}` : ''}:</strong> <p style={{ margin: '5px 0 0 0', fontSize: '13px' }}> {formatReservationDetailsForDisplay(editingTableInfo.currentBookingDetails)} </p> </div> )} <div style={inputContainerStyle}> <label htmlFor="editTableStatus" style={labelStyle}>Set Inherent Table Status to:</label> <select id="editTableStatus" name="newStatus" value={editingTableInfo.newStatus} onChange={handleIndividualTableStatusChange} style={inputFieldStyle}> {tableStatuses.map(status => <option key={status} value={status}>{status}</option>)} </select> {editingTableInfo.newStatus === 'Available' && editingTableInfo.currentBookingDetails && editingTableInfo.currentBookingDetails.id && <p style={{ fontSize: '12px', color: 'orange', marginTop: '5px' }}>Warning: Setting to 'Available' and confirming the prompt may cancel the highlighted booking: "{editingTableInfo.currentBookingDetails.name}" for {editingTableInfo.currentBookingDetails.date} at {editingTableInfo.currentBookingDetails.time}.</p>} {editingTableInfo.newStatus === 'Cleaning' && <p style={{ fontSize: '12px', color: '#5bc0de', marginTop: '5px' }}>Note: Table will show 'Cleaning' if no overriding booking is active at the selected global time.</p>} {editingTableInfo.newStatus === 'Occupied' && <p style={{ fontSize: '12px', color: statusColors.Occupied, marginTop: '5px' }}>Note: Table will show 'Occupied' unless a current booking specifically puts it into 'Cleaning' phase.</p>} </div> <div style={buttonContainerStyle}> <button onClick={submitIndividualTableStatus} style={{ ...buttonStyle, width: '180px' }}> <FaSave style={{marginRight:'8px'}}/>Update Status</button> </div> </div> )}
            
            {showAddToWaitlistModal && (
                <div style={{ ...modalStyle, maxWidth: '450px' }}>
                    <div style={modalHeaderStyle}>
                        <h3 style={{ margin: 0, fontSize: '18px' }}>Add Guest to Waitlist</h3>
                        <button style={modalCloseButtonStyle} onClick={() => setShowAddToWaitlistModal(false)} title="Close"><FaTimes /></button>
                    </div>
                    <div style={inputContainerStyle}>
                        <label htmlFor="waitlistGuestName" style={labelStyle}>Guest Name:<span style={{ color: 'red' }}>*</span></label>
                        <input id="waitlistGuestName" type="text" value={newWaitlistEntryName} onChange={(e) => setNewWaitlistEntryName(e.target.value)} style={inputFieldStyle} />
                    </div>
                    <div style={inputRowStyle}>
                        <div style={inputContainerHalfStyle}>
                            <label htmlFor="waitlistPartySize" style={labelStyle}>Party Size:<span style={{ color: 'red' }}>*</span></label>
                            <input id="waitlistPartySize" type="number" min="1" value={newWaitlistEntryPartySize} onChange={(e) => setNewWaitlistEntryPartySize(e.target.value)} style={inputFieldStyle} />
                        </div>
                        <div style={inputContainerHalfStyle}>
                            <label htmlFor="waitlistGuestPhone" style={labelStyle}>Phone (Optional):</label>
                            <input id="waitlistGuestPhone" type="tel" value={newWaitlistEntryPhone} onChange={(e) => setNewWaitlistEntryPhone(e.target.value)} style={inputFieldStyle} />
                        </div>
                    </div>
                    <div style={buttonContainerStyle}>
                        <button onClick={handleSaveWaitlistEntry} style={{ ...buttonStyle, width: '180px', backgroundColor: '#17a2b8' }}>
                            <FaUserPlus style={{ marginRight: '8px' }} /> Add to Waitlist
                        </button>
                    </div>
                </div>
            )}

            {showSeatWaitlistGuestModal && seatingWaitlistGuestInfo && (
                 <div style={{ ...modalStyle, maxWidth: '500px' }}>
                    <div style={modalHeaderStyle}>
                        <h3 style={{ margin: 0, fontSize: '18px' }}>Seat Waitlisted Guest</h3>
                        <button style={modalCloseButtonStyle} onClick={() => {setShowSeatWaitlistGuestModal(false); setSeatingWaitlistGuestInfo(null);}} title="Close"><FaTimes /></button>
                    </div>
                    <div style={detailsBoxStyle}>
                        <p><strong>Guest:</strong> {seatingWaitlistGuestInfo.entry.name}</p>
                        <p><strong>Party Size:</strong> {seatingWaitlistGuestInfo.entry.partySize}</p>
                        <p><strong>Phone:</strong> {seatingWaitlistGuestInfo.entry.phone || 'N/A'}</p>
                    </div>

                    <div style={inputContainerStyle}>
                        <label htmlFor="selectTableForWaitlist" style={labelStyle}>Select Available Table (Capacity {'>='} {seatingWaitlistGuestInfo.entry.partySize}):</label>
                        {availableTablesForWaitlistGuest.length > 0 ? (
                            <select 
                                id="selectTableForWaitlist" 
                                value={selectedTableForWaitlistSeating} 
                                onChange={(e) => setSelectedTableForWaitlistSeating(e.target.value)} 
                                style={inputFieldStyle}
                            >
                                <option value="">-- Select a Table --</option>
                                {availableTablesForWaitlistGuest.map(table => (
                                    <option key={table.id} value={table.id}>
                                        {table.shortId} (Cap: {table.capacityValue}, Floor: {table.floorName})
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <p style={{color: statusColors.Occupied, fontSize: '13px', marginTop: '5px'}}>No suitable tables are currently available based on global filters.</p>
                        )}
                    </div>
                     <p style={{fontSize:'11px', color:'#333', marginTop:'-8px', marginBottom:'10px'}}>Seating at: {actualCurrentDate} {actualCurrentTime} for {DEFAULT_WALK_IN_SEATING_DURATION_MINUTES} mins (Restaurant Hours: {RESTAURANT_OPEN_TIME_STR} - {RESTAURANT_CLOSE_TIME_STR}).</p>


                    <div style={buttonContainerStyle}>
                        <button 
                            onClick={handleConfirmSeatWaitlistGuest} 
                            style={{ ...buttonStyle, width: '180px', backgroundColor: statusColors.Available }}
                            disabled={!selectedTableForWaitlistSeating || availableTablesForWaitlistGuest.length === 0}
                        >
                            <FaBookmark style={{ marginRight: '8px' }} /> Seat Guest
                        </button>
                    </div>
                </div>
            )}

            { (showFloorEditModal || showBookTableModal || showIndividualTableModal || showAddToWaitlistModal || showSeatWaitlistGuestModal) && <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 999 }} /> }
        </div>
    );
};

// Styles
const pageContainerStyle = {
    minHeight: '100vh',
    display: 'flex', 
    flexDirection: 'row', 
    width: '100%',
};

const mainTableSectionStyle = { 
    flexGrow: 7, 
    flexShrink: 1,
    flexBasis: '0%',
    boxSizing: 'border-box',
    display: 'flex', 
    flexDirection: 'column',
    overflowY: 'auto', 
    height: '100vh', 
    scrollbarWidth: 'none', 
    scrollbarColor: '#b15cd6 #f0f0f0', 
    padding: '15px',
};

const bookingsViewSectionStyle = { 
    flexGrow: 3, 
    flexShrink: 1,
    flexBasis: '0%',
    padding: '15px',
    boxSizing: 'border-box',
    overflowY: 'auto',
    height: '100vh',
    borderLeft: '1px solid #ddd', 
    backgroundColor: '#fdfdff', 
    scrollbarWidth: 'thin',
    scrollbarColor: '#a0a0a0 #f0f0f0', 
};

const globalControlsContainerStyle = { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', padding: '10px 15px', backgroundColor: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6', flexWrap: 'wrap', flexShrink: 0 };
const globalControlItemStyle = { display: 'flex', alignItems: 'center', gap: '8px'};
const legendContainerStyle = { display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 0px', fontSize: '11px', flexWrap: 'wrap', lineHeight: '1.2', justifyContent:'center', marginBottom:'10px', flexShrink: 0 };
const legendTitleStyle = { margin: 0, marginRight: '3px', fontWeight: '500', fontSize: '12px' };
const legendItemStyle = { display: 'flex', alignItems: 'center', gap: '4px' };
const legendColorBoxStyle = { width: '12px', height: '12px', borderRadius: '3px', border: '1px solid rgba(0,0,0,0.2)' };

const sharedButtonStyles = { border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px', fontWeight: "bold", padding: '0 15px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s ease-in-out, box-shadow 0.1s ease' };
const buttonStyle = { ...sharedButtonStyles, height: "36px", backgroundColor: '#b15cd6', color: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' };

const smallButtonStyle = {
    ...sharedButtonStyles,
    height: 'auto',
    fontSize: '12px', fontWeight: "500", padding: '6px 10px', lineHeight: 1.3
};

const smallActionButtonStyle = {
    ...sharedButtonStyles,
    height: 'auto',
    minWidth: 'auto',
    fontSize: '11px',
    fontWeight: '500',
    padding: '4px 8px',
    lineHeight: 1.2,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
};


const tableHeaderStyle = { padding: '10px 8px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#b15cd6', color: 'white', position: 'sticky', top: 0, zIndex: 1, fontSize: '13px', whiteSpace: 'nowrap' };
const tableCellStyle = { padding: '6px 8px', border: '1px solid #ddd', textAlign: 'center', verticalAlign: 'middle', fontSize: '13px' };
const evenRowStyle = { backgroundColor: '#fdf6ff' };
const oddRowStyle = { backgroundColor: '#fff' };

const actionButtonStyle = { backgroundColor: 'transparent', color: '#555', border: 'none', cursor: 'pointer', borderRadius: '5px', margin: '0 2px', fontSize: "17px", padding: '5px', lineHeight: 1, display: 'inline-flex', alignItems: 'center', transition: 'color 0.2s' };

const modalStyle = { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'white', padding: '20px 25px', borderRadius: '10px', boxShadow: '0 5px 20px rgba(0, 0, 0, 0.25)', zIndex: 1000, width: '90%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box', scrollbarWidth: 'thin', scrollbarColor: '#b15cd6 #f0f0f0' };
const modalHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' };
const modalCloseButtonStyle = { background: 'transparent', border: 'none', fontSize: '20px', color: '#777', cursor: 'pointer', padding: '5px' };

const inputContainerStyle = { marginBottom: '12px' };
const labelStyle = { display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '13px', color: '#333' };
const inputFieldStyle = { width: '100%', padding: '9px 10px', fontSize: '13px', borderRadius: '5px', border: '1px solid #ccc', boxSizing: 'border-box', transition: 'border-color 0.2s, box-shadow 0.2s' };

const buttonContainerStyle = { display: 'flex', justifyContent: 'center', marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #eee' };
const paginationButtonStyle = { ...sharedButtonStyles, height: "32px", minWidth: "70px", backgroundColor: "rgb(177, 92, 214)", color: "#fff", margin: '0 5px', padding: '0 10px' };

const tableVisualContainerStyle = { display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '5px 0', justifyContent: 'flex-start', alignItems: 'center' };
const individualTableStyle = {
    width: '35px', height: '35px', borderRadius: '4px',
    border: '1px solid rgba(0,0,0,0.15)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold', color: 'white',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    WebkitTextStroke: '0.5px rgba(0,0,0,0.5)', textStroke: '0.5px rgba(0,0,0,0.5)',
    flexShrink: 0,
    cursor: 'pointer',
};

const tableRepresentationWrapperStyleNoHighlight = {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '3px',
    borderRadius: '4px',
    minWidth: 'auto',
};

const inputRowStyle = { display: 'flex', gap: '15px', marginBottom: '12px' };
const inputContainerHalfStyle = { flex: 1 };
const detailsBoxStyle = { backgroundColor: '#f9f9f9', border: '1px solid #eee', borderRadius: '5px', padding: '8px 10px', marginBottom: '12px', fontSize: '13px', color: '#333', lineHeight: '1.5' };

const configSectionStyle = {
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    marginBottom: '15px',
    backgroundColor: '#fdfdff',
    padding: '12px 15px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
};
const configHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '10px',
    marginBottom: '10px',
    borderBottom: '1px solid #eaeaea',
    fontSize: '14px',
    fontWeight: '500'
};
const individualTableListStyleInModal = {
    maxHeight: '200px',
    overflowY: 'auto',
    paddingRight: '8px',
    marginRight: '-8px',
    scrollbarWidth: 'thin',
    scrollbarColor: '#b15cd6 #f0f0f0',
};
const individualTableEditRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 3px',
    borderBottom: '1px dotted #f0f0f0',
    fontSize: '13px',
};

export default Tablereservations;